import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { BiArrowToTop } from "react-icons/bi";
import { GrConfigure } from 'react-icons/gr';
import './CapperNode.css'; // Import the CSS file
import CapperConfigModal from './CapperConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const CapperNode = ({ id, data, selected }) => {
  const { acdcRatio, colName, name, KA, MH, genToTransLoss, capLimit } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addCapperNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`CapperNode ${id} received data:`, data);
    // Log specific column name information for debugging
    console.log(`CapperNode ${id} column name details:`, {
      colNameFromProps: colName,
      colNameFromData: data.colName,
      colNameType: typeof data.colName,
      isEmpty: data.colName === '',
      isUndefined: data.colName === undefined
    });
    console.log(`CapperNode ${id} region/loss:`, { KA, MH, genToTransLoss });
  }, [id, data, colName, KA, MH, genToTransLoss]);

  // Check if node is configured properly
  // With auto-select, colName may be blank; treat acdcRatio + name as sufficient
  const isConfigured = data.isConfigured || (!!name && !!acdcRatio);
  
  // Check if the node needs reconfiguration
  const needsReconfiguration = data.isApiConfigured === false || data.isConfigured === false;
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`CapperNode ${id} state:`, {
      isConfigured,
      needsReconfiguration,
      name,
      acdcRatio,
      colName,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, needsReconfiguration, name, acdcRatio, colName, data]);

  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  
  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      
      addCapperNode(
        name,
        acdcRatio,
        colName,
        inputNodeName,
        { x: position.x, y: position.y }
      );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    // Get the source node ID
    const sourceNodeId = inputEdge.source;
    
    // Find the actual source node in the nodes array
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    if (!sourceNode) return sourceNodeId;
    
    // If the source node has a name in its data, use that
    if (sourceNode.data && sourceNode.data.name) {
      console.log(`Using source node name from data: ${sourceNode.data.name}`);
      return sourceNode.data.name;
    }
    
    // Get the node mapping
    const nodeMapping = getNodeMapping();
    
    // Get the actual node name from the mapping, or fall back to the ID
    const actualNodeName = nodeMapping[sourceNodeId] || sourceNodeId;
    
    console.log(`CapperNode ${id} input connection:`, {
      sourceNodeId,
      sourceNodeData: sourceNode.data,
      nodeMapping,
      actualNodeName
    });
    
    return actualNodeName;
  }, [edges, id, nodes, getNodeMapping]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      acdcRatio,
      colName,
      isModalOpen
    });
    
    if (!hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // This allows editing an existing node even if the input is temporarily disconnected
      notifyError(new Error("Please connect an input to this capper node before configuring it."), {
        toastId: 'capper.connectInput',
      });
      return;
    }
    
    // Open the full configuration modal
    console.log(`Opening modal for ${needsReconfiguration ? 'reconfiguration' : isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, acdcRatio, colName, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, acdcRatio, colName, KA: cfgKA, MH: cfgMH, genToTransLoss: cfgLoss, capLimit: cfgCapLimit } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring capper node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
      // Add the capper node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Find the source node in the nodes array to get its actual name
        const inputEdge = edges.find(edge => edge.target === id);
        const sourceNodeId = inputEdge ? inputEdge.source : null;
        const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
        
        // Determine the actual input node name to use
        let actualInputNodeName = inputNodeName;
        
        // If the source node has a name in its data, use that
        if (sourceNode && sourceNode.data && sourceNode.data.name) {
          actualInputNodeName = sourceNode.data.name;
          console.log(`Using source node name from data: ${actualInputNodeName}`);
        }
        
        // Check if this is a reconfiguration (node already exists but input changed)
        // Only consider it a reconfiguration if needsReconfiguration is true
        // This prevents treating regular edits as reconfigurations
        const isReconfiguration = needsReconfiguration;
        
        console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} capper node to session:`, {
          name,
          acdcRatio,
          colName,
          inputNodeName: actualInputNodeName,
          position,
          isReconfiguration
        });
        
        // Check if this is a first-time configuration, a reconfiguration, or an edit
        const isFirstTimeConfig = !data.isConfigured && !data.isApiConfigured;
        const isEditingExistingNode = data.isConfigured && !needsReconfiguration;
        
        console.log('Configuration type:', {
          isFirstTimeConfig,
          isReconfiguration,
          isEditingExistingNode,
          isConfigured: data.isConfigured,
          isApiConfigured: data.isApiConfigured,
          needsReconfiguration
        });
        
        // For first-time configuration, the modal component has already added the node to the pipeline
        if (isFirstTimeConfig && session.pipeline_name) {
          console.log(`First-time configuration for node ${name} with input ${actualInputNodeName}`);
          // No need to call the API here, it was already called by the modal component
        }
        // If this is a reconfiguration, use the reconfigure API
        else if (isReconfiguration && session.pipeline_name) {
          console.log(`Calling reconfigure API for node ${name} with new source ${actualInputNodeName}`);
          
          try {
            console.log(`Reconfiguring node ${name} with new input:`, {
              oldInput: data.inputNodeName,
              newInput: actualInputNodeName
            });
            
            // Call the API to reconfigure the node
            await reconfigureNode(
              session.pipeline_name,
              name,
              actualInputNodeName
            );
            
            console.log(`Node ${name} reconfigured successfully with new input ${actualInputNodeName}`);
          } catch (reconfigError) {
            console.error('Error reconfiguring node:', reconfigError);
            // Don't continue with local updates if API call fails
            throw reconfigError; // Re-throw the error to be caught by the outer try-catch
          }
        }
        // If this is an edit of an existing node, we don't need to call any API here
        // The editCapperNode API was already called by the CapperConfigModal component
        else if (isEditingExistingNode) {
          console.log(`Editing existing node ${name} - API call already handled by modal`);
        }
        
        // Now add/update the node in the session with the correct name
        addCapperNode(
          name,
          acdcRatio,
          colName,
          actualInputNodeName,
          { x: position.x, y: position.y },
          {
            KA: cfgKA,
            MH: cfgMH,
            genToTransLoss: cfgLoss,
            capLimit: cfgCapLimit
          }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  acdcRatio,
                  colName,
                  KA: cfgKA,
                  MH: cfgMH,
                  genToTransLoss: cfgLoss,
                  capLimit: cfgCapLimit,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  acdcRatio,
                  colName,
                  KA: cfgKA,
                  MH: cfgMH,
                  genToTransLoss: cfgLoss,
                  capLimit: cfgCapLimit,
                  isConfigured: true,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring capper node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editCapper });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addCapperNode, session, updateSelectedNode, edges, nodes, data, needsReconfiguration]);

  return (
    <>
      <div
        className={`capper-node ${selected ? 'capper-selected' : needsReconfiguration ? 'capper-needs-reconfiguration' : isConfigured ? 'capper-configured' : 'capper-not-configured'}`}
      >
        <div className={`capper-node-header ${needsReconfiguration ? 'capper-needs-reconfiguration' : isConfigured ? 'capper-configured' : 'capper-not-configured'}`}>
          <div className="capper-node-header-title">
            <div className="capper-node-type">Capper</div>
            {name && (isConfigured || needsReconfiguration) && <div className="capper-node-name-display">{name}</div>}
          </div>
          <div className="capper-node-header-icons">
            {/* Show different buttons based on node state */}
            {needsReconfiguration ? (
              <button 
                onClick={handleReconfigureInput}
                className="capper-config-button capper-reconfigure-button"
                title="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="capper-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="capper-config-button"
                title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Capper" : "Connect an input first"}
                disabled={(!hasInputConnection && !isConfigured) || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="capper-icon" />
                ) : (
                  <ExclamationTriangleIcon className="capper-icon" />
                )}
              </button>
            )}
            <BiArrowToTop className="capper-icon" />
          </div>
        </div>
        <div className="capper-node-content">
          <div className="capper-text-xs">
            {!hasInputConnection && !isConfigured && (
              <div className="capper-text-warning">
                Connect an input to configure
              </div>
            )}
            {hasInputConnection && !isConfigured && (
              <div className="capper-text-warning">
                Parameters not configured
              </div>
            )}
            {needsReconfiguration && isConfigured && (
              <div className="capper-text-warning">
                Input needs reconfiguration
              </div>
            )}
            {isConfiguring && (
              <div className="capper-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>AC/DC Ratio: {acdcRatio ?? data.acdcRatio ?? 'N/A'}</div>
                <div>Column: {
                  colName && colName !== 'Column name not specified' ? colName : (data.colName && data.colName !== 'Column name not specified' ? data.colName : 'Auto')
                }</div>
                <div>Cap Limit: {capLimit !== null && capLimit !== undefined ? `${capLimit} (Manual)` : 'Auto'}</div>
              </>
            )}
          </div>
          <div className="capper-flex-container">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`capper-connection-point ${
                needsReconfiguration ? 'capper-needs-reconfiguration' : 
                isConfigured ? 'capper-configured' : 
                'capper-not-configured'
              }`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`capper-connection-point ${
                needsReconfiguration ? 'capper-needs-reconfiguration-output' : 
                isConfigured ? 'capper-configured-output' : 
                'capper-not-configured'
              }`}
              isConnectable={isConfigured || needsReconfiguration} // Allow connections if configured or needs reconfiguration
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <CapperConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={needsReconfiguration}
        initialData={{ 
          name, 
          acdcRatio,
          colName,
          capLimit,
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
          isConfigured: data.isConfigured,
          isEditing: isConfigured && !needsReconfiguration,
          isReconfiguring: needsReconfiguration,
          KA,
          MH,
          genToTransLoss
        }}
      />
    </>
  );
};

export default CapperNode;